import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiTableLight,
  PiPlusLight,
  PiTrashLight,
  PiPlayLight,
  PiDownloadSimpleLight,
  PiArrowCounterClockwiseLight,
  PiSpinnerGapLight,
  PiHourglassLight,
  PiClockLight,
  PiClockCounterClockwiseLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  fetchBatchTables,
  deleteBatchTable,
  deleteBatchTablesBulk,
  startProcessing,
  resetTable,
  exportTable,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { TableListItem, TableStatus } from '../../types/batchTables';
import CreateTableForm from './CreateTableForm';
import DeleteTableConfirmation from './DeleteTableConfirmation';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import Button from '../shared/TailwindComponents/Button';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { useProcessingPubSub } from '../../hooks/useProcessingPubSub';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tablesListTourSteps } from '../shared/FeatureTour';

// ─── Status → Badge color ────────────────────────────────────────────────────

const statusColorMap: Record<TableStatus, BadgeColor> = {
  pending: 'gray',
  queued: 'orange',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  partial: 'yellow',
};

const statusLabelMap: Record<TableStatus, string> = {
  pending: 'Pending',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  partial: 'Partial',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Component ───────────────────────────────────────────────────────────────

const BatchTablesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  // Redux state
  const {
    items,
    totalItems,
    currentPage,
    totalPages,
    isLoading,
  } = useSelector((state: RootState) => state.batchTables);

  const { checkSectionGate } = useLicenseGate();

  // Local state
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const [isCreating, setIsCreating] = useState(() => {
    if (queryParams.get('add') === 'true') {
      return checkSectionGate('tables');
    }
    return false;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);

  // ─── PubSub: live processing tracking ──────────────────────────────
  const fetchRef = useRef<() => void>(() => {});
  const activeStatuses = new Set<TableStatus>(['processing', 'queued', 'pending']);

  const { processingStates, joinProcessingGroups, reattachListener } = useProcessingPubSub({
    entityType: 'table',
    getGroupName: (item: TableListItem) => item.uuid, // tables use UUID directly as PubSub group
    getItemUuid: (item: TableListItem) => item.uuid,
    isProcessing: (item: TableListItem) => activeStatuses.has(item.status),
    onRefetch: () => fetchRef.current(),
    enabled: !isCreating,
  });

  // ─── Data Fetching (server-side search + sort) ──────────────────────────

  const fetchPage = useCallback(
    (page: number) => {
      dispatch(
        fetchBatchTables({
          page,
          page_size: 20,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...(searchTerm ? { search_term: searchTerm } : {}),
        })
      );
    },
    [dispatch, sortBy, sortOrder, searchTerm]
  );

  // Keep fetchRef in sync for PubSub refetch callback
  useEffect(() => {
    fetchRef.current = () => fetchPage(1);
  }, [fetchPage]);

  // Join PubSub groups whenever items update
  useEffect(() => {
    if (!isCreating && items.length > 0) {
      joinProcessingGroups(items);
    }
  }, [items, isCreating, joinProcessingGroups]);

  // Re-attach PubSub listener when coming back from creation form
  const prevIsCreatingRef = useRef(isCreating);
  useEffect(() => {
    if (prevIsCreatingRef.current && !isCreating) {
      reattachListener();
      fetchPage(1);
    }
    prevIsCreatingRef.current = isCreating;
  }, [isCreating, fetchPage, reattachListener]);

  // Re-fetch from page 1 whenever sort or search changes
  useEffect(() => {
    if (isCreating) return;
    fetchPage(1);
  }, [fetchPage, isCreating]);

  // Poll for status updates when items are in processing state
  useEffect(() => {
    if (isCreating) return;
    const hasProcessingItems = items.some((item: TableListItem) => activeStatuses.has(item.status));
    if (!hasProcessingItems) return;

    const interval = setInterval(() => {
      fetchPage(1);
    }, 8000);

    return () => clearInterval(interval);
  }, [items, isCreating, fetchPage]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'tables-list',
    steps: tablesListTourSteps,
    readyToShow: !isLoading && items.length > 0,
    autoStartDelay: 600,
  });

  const hasMore = currentPage < totalPages;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchPage(currentPage + 1);
    }
  }, [fetchPage, currentPage, hasMore, isLoading]);

  // Debounced search handler
  const handleSearchChange = useCallback(
    debounce((query: string) => {
      setSearchTerm(query);
    }, 300),
    []
  );

  // ─── Table columns & data ──────────────────────────────────────────────

  const columns = ['Table Name', 'Status', 'Documents', 'Columns', 'Access', 'Created', 'Actions'];

  const tableData = useMemo(
    () =>
      items.map((item: TableListItem) => {
        const liveState = processingStates[item.uuid];
        const isProcessing = item.status === 'processing';
        const isPending = item.status === 'pending';
        const isQueued = item.status === 'queued';
        const isFailed = item.status === 'failed';
        const isCompleted = item.status === 'completed';
        const isClickable = isCompleted || item.status === 'partial';

        return {
          id: item.uuid,
          uuid: item.uuid,
          processing_status: item.status,
          _isClickable: isClickable,
          'Table Name': (
            <div className="flex flex-col gap-0.5" style={{ opacity: isFailed ? 0.6 : 1 }}>
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <MiniLoadingToCheckmark isLoading={true} />
                  <div className="flex-1 min-w-0">
                    {liveState?.step ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium truncate">
                            {(liveState.step || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        {liveState.message && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {liveState.message}
                          </div>
                        )}
                        {liveState.percent != null && liveState.percent > 0 && (
                          <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-700 transition-all duration-700 ease-in-out rounded-full"
                              style={{ width: `${liveState.percent}%` }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-500 dark:text-gray-400 italic">Processing table...</div>
                        <div className="h-3 w-2/3 bg-gray-300 dark:bg-gray-600 rounded mt-1 animate-pulseFade" />
                      </>
                    )}
                  </div>
                </div>
              ) : isFailed ? (
                <div className="flex items-center gap-2">
                  <div className="text-sm text-red-500 dark:text-red-400 italic">Processing failed</div>
                </div>
              ) : (isPending || isQueued) ? (
                <div className="flex items-center gap-2">
                  <PiHourglassLight className="h-4 w-4 text-orange-400 animate-pulse flex-shrink-0" />
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      {isPending ? 'Waiting to be queued...' : 'Queued for processing...'}
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
              )}
            </div>
          ),
          Status: (
            <div className="flex items-center gap-2">
              <Badge
                color={statusColorMap[item.status] || 'gray'}
                icon={
                  isProcessing ? <PiSpinnerGapLight className="animate-spin" /> :
                  (isPending || isQueued) ? <PiClockLight className="text-orange-400" /> :
                  undefined
                }
              >
                {statusLabelMap[item.status] || item.status}
              </Badge>
            </div>
          ),
        Documents: (
          <Badge color="blue">
            {item.document_count}
          </Badge>
        ),
        Columns: (
          <Badge color="gray">
            {item.column_count}
          </Badge>
        ),
        'Access': item.has_access && item.has_access.length > 0 ? (
          <div className="flex -space-x-1.5">
            {item.has_access.slice(0, 5).map((user: any, idx: number) => (
              <div key={idx} className={clsx(idx > 0 && 'ml-[-0.5rem]')} style={{ zIndex: 5 - idx }}>
                <CollaboratorBadge collaborator={user} />
                </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
        Created: formatDate(item.created_at),
      };
    }),
    [items, processingStates]
  );

  const sortableColumns = ['Table Name', 'Status', 'Documents', 'Columns', 'Created'];

  // ─── Actions ───────────────────────────────────────────────────────────

  const handleRowClick = useCallback(
    (row: any) => {
      const status = row.processing_status;
      if (status && status !== 'completed' && status !== 'partial') {
        if (status === 'processing') {
          dispatch(showFeedbackModal({
            showModal: true,
            message: 'This table is still being processed. Please wait until processing completes.',
            modalType: 'info',
            duration: 3000,
          }));
        } else if (status === 'failed') {
          dispatch(showFeedbackModal({
            showModal: true,
            message: 'This table failed to process.',
            modalType: 'warning',
            duration: 3000,
          }));
        }
        return;
      }
      history.push(`/tables/${row.uuid}`);
    },
    [history, dispatch]
  );

  const handleDelete = useCallback(
    (uuid: string) => {
      dispatch(
        showModal({
          type: 'custom',
          message: t('Are you sure you want to delete this table?'),
          subMsg: t('This action cannot be undone.'),
          icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
          width: '500px',
          content: (
            <DeleteTableConfirmation
              count={1}
              onConfirm={async () => {
                await dispatch(deleteBatchTable(uuid) as any);
                dispatch(hideModal());
              }}
              onCancel={() => dispatch(hideModal())}
            />
          ),
          onCancel: () => {},
          showModal: true,
        })
      );
    },
    [dispatch, t]
  );

  const handleReprocess = useCallback(
    async (uuid: string) => {
      await dispatch(startProcessing(uuid) as any);
    },
    [dispatch]
  );

  const handleReset = useCallback(
    (uuid: string) => {
      dispatch(
        showModal({
          type: 'confirmation',
          message: t('Reset Table'),
          subMsg: t('All processed results will be cleared and the table will return to pending status.'),
          showModal: true,
          onConfirm: async () => {
            await dispatch(resetTable(uuid) as any);
            dispatch(hideModal());
          },
          onCancel: () => dispatch(hideModal()),
        })
      );
    },
    [dispatch]
  );

  const handleExport = useCallback(
    async (uuid: string) => {
      await dispatch(exportTable(uuid, 'csv') as any);
    },
    [dispatch]
  );

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    dispatch(
      showModal({
        type: 'custom',
        message: t(`Are you sure you want to delete ${selectedIds.length > 1 ? 'these tables' : 'this table'}?`),
        subMsg: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        width: '500px',
        content: (
          <DeleteTableConfirmation
            count={selectedIds.length}
            onConfirm={async () => {
              if (selectedIds.length > 1) {
                await dispatch(deleteBatchTablesBulk(selectedIds) as any);
              } else {
                await dispatch(deleteBatchTable(selectedIds[0]) as any);
              }
              setSelectedIds([]);
              dispatch(hideModal());
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true,
      })
    );
  }, [dispatch, selectedIds, t]);

  const tableActions = useMemo(
    () => [
      {
        label: t('Activity'),
        onClick: (row: any) => {
          const item = items.find((i: TableListItem) => i.uuid === row.uuid);
          setAuditTarget({ type: 'table', id: row.uuid || row.id, name: item?.name || '' });
        },
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
      },
      // {
      //   label: t('Rerun'),
      //   onClick: (row: any) => handleReprocess(row.uuid),
      //   icon: <PiPlayLight className="w-4 h-4" />,
      // },
      {
        label: t('Export CSV'),
        onClick: (row: any) => handleExport(row.uuid),
        icon: <PiDownloadSimpleLight className="w-4 h-4" />,
      },
      // {
      //   label: t('Reset'),
      //   onClick: (row: any) => handleReset(row.uuid),
      //   icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
      // },
      {
        label: t('Delete'),
        onClick: (row: any) => handleDelete(row.uuid),
        icon: <PiTrashLight className="w-4 h-4" />,
        isDestructive: true,
      },
    ],
    [handleDelete, handleReprocess, handleExport, handleReset, t, items]
  );

  const filterBarActions = useMemo(() => {
    const actions = [];
    if (selectedIds.length > 0) {
      actions.push({
        label: `Delete (${selectedIds.length})`,
        onClick: handleBulkDelete,
        icon: <PiTrashLight className="w-4 h-4" />,
      });
    }
    return actions;
  }, [selectedIds, handleBulkDelete]);

  const handleSortChange = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      // The Table component sends lowercase+underscore column names
      // Map them to the backend sort_by field names
      const columnToFieldMap: Record<string, string> = {
        'table_name': 'table_name',
        'status': 'status',
        'documents': 'documents',
        'columns': 'columns',
        'progress': 'progress',
        'created': 'created',
      };
      setSortBy(columnToFieldMap[column] || column);
      setSortOrder(order);
    },
    []
  );

  // ─── Create Modal ──────────────────────────────────────────────────────

  const handleCreateClose = useCallback(
    (newUuid?: string) => {
      setIsCreating(false);
      if (newUuid) {
        history.push(`/tables/${newUuid}`);
      } else {
        history.replace('/tables');
      }
    },
    [history]
  );

  // ─── Render ────────────────────────────────────────────────────────────

  if (isCreating) {
    return (
      <div className="flex flex-col overflow-hidden relative h-screen">
        <CreateTableForm onClose={handleCreateClose} />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden relative">
      <div data-tour-id="tables-list-new-button">
        <Header
          actions={[
            {
              label: t('New Table'),
              icon: <PiPlusLight className="w-4 h-4" />,
              onClick: () => setIsCreating(true),
            },
          ]}
        />
      </div>

      <div data-tour-id="tables-list-filter-bar">
      <FilterBarWithTabs
        onSearchChange={handleSearchChange}
        // viewMode={viewMode}
        // setViewMode={setViewMode}
        actions={[
          ...filterBarActions,
          ...(showTrash ? trashBulkActions : []),
        ]}
        selectionCount={showTrash ? trashSelectedCount : (selectedIds.length > 0 ? selectedIds.length : undefined)}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Tables')}
        onRecycleBinToggle={(active) => { if (!active) fetchPage(1); setShowTrash(active); }}
        // onRefresh={() => fetchPage(1)}
        // itemCount={items.length}
        // totalItemCount={totalItems}
        // itemCountLabel="tables"
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          entityType="table"
          entityLabel="tables"
          emptyIcon={<PiTableLight />}
          onItemRestored={() => fetchPage(1)}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      <div className="flex-grow overflow-y-auto relative">
        {/* Tour anchor: small overlay covering table header + first row area */}
        <div
          data-tour-id="tables-list-table"
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: 120 }}
        />
        <div className="animate-fade-in">
          <Table
            columns={columns}
            data={tableData}
            enableSelection
            selectedIds={selectedIds}
            onSelect={(id) =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            onSelectAll={() =>
              setSelectedIds(
                selectedIds.length === items.length
                  ? []
                  : items.map((item: TableListItem) => item.uuid)
              )
            }
            onRowClick={handleRowClick}
            actions={tableActions}
            hasMore={hasMore}
            isLoadingMore={isLoading}
            onLoadMore={loadMore}
            sortableColumns={sortableColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            total_items={totalItems}
            total_showing_items={items.length}
            setIsShowing={true}
            showingLabel={t('tables')}
            selectedCount={selectedIds.length}
            resizableColumns
            storageKeyPrefix={t("tables")}
            max = 'max-h-[calc(100vh-120px)]'
            min = 'min-h-[calc(100vh-120px)]'
            defaultColumnWidths={{
              'Table Name': 250,
              Status: 120,
              Documents: 110,
              Columns: 100,
              Progress: 140,
              'Has Access': 140,
              Created: 140,
            }}
            emptyState={{
              icon: <PiTableLight className="w-12 h-12" />,
              headline: t('No tables yet'),
              subtext: t(
                'Create a table to analyze batches of documents with AI-powered queries.'
              ),
              button: {
                label: t('Create First Table'),
                onClick: () => setIsCreating(true),
              },
            }}
          />
        </div>
      </div>
      )}
      {/* Audit Log Side Panel */}
      {auditTarget && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300" onClick={() => setAuditTarget(null)} />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType={auditTarget.type}
              entityId={auditTarget.id}
              entityName={auditTarget.name}
              onClose={() => setAuditTarget(null)}
            />
          </div>
        </>
      )}

      <FeatureTour
        steps={tablesListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default BatchTablesList;
